/**
 * React context provider for feature flags.
 * Provides feature flag values and utilities to React components.
 */

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FeatureFlag,
  type FeatureFlagContextValue,
  type FeatureFlagProviderProps,
  type FeatureFlagValues,
} from './types';
import { FeatureFlagManager } from './manager';
import { FEATURE_FLAG_CONFIG } from './config';

/**
 * React context for feature flags.
 * Use the useFeatureFlags hook to access this context.
 */
export const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(
  null
);

/**
 * Custom error boundary for feature flag provider errors.
 */
class FeatureFlagErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: {
    children: React.ReactNode;
    fallback?: React.ReactNode;
  }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[FeatureFlags] Provider error:', error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role='alert'
          style={{ padding: '16px', color: 'red', fontFamily: 'monospace' }}
        >
          <h3>Feature Flag Provider Error</h3>
          <p>There was an error initializing feature flags.</p>
          <details>
            <summary>Error Details</summary>
            <pre>{this.state.error?.message}</pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Feature flag provider component.
 * Wraps your app to provide feature flag functionality to all child components.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <FeatureFlagProvider>
 *       <MyApp />
 *     </FeatureFlagProvider>
 *   );
 * }
 * ```
 */
export function FeatureFlagProvider({
  children,
  config,
  testValues,
}: FeatureFlagProviderProps) {
  const [manager] = useState(() => FeatureFlagManager.getInstance(config));
  const [isInitialized, setIsInitialized] = useState(false);
  const [flags, setFlags] = useState<FeatureFlagValues>(
    {} as FeatureFlagValues
  );

  // Initialize the manager and set up flag values
  useEffect(() => {
    try {
      // Initialize the manager
      manager.initialize();

      // Get flag values (with optional test overrides)
      let flagValues = manager.getAll();

      // Apply test value overrides if provided
      if (testValues) {
        flagValues = { ...flagValues, ...testValues };
      }

      setFlags(flagValues);
      setIsInitialized(true);
    } catch (error) {
      console.error('[FeatureFlags] Failed to initialize:', error);
      // Use default values as fallback
      const defaultFlags = Object.entries(FEATURE_FLAG_CONFIG).reduce(
        (acc, [flag, config]) => {
          acc[flag as FeatureFlag] = config.defaultValue;
          return acc;
        },
        {} as FeatureFlagValues
      );

      // Apply test value overrides to defaults if provided
      if (testValues) {
        Object.assign(defaultFlags, testValues);
      }

      setFlags(defaultFlags);
      setIsInitialized(true);
    }
  }, [manager, testValues]);

  // Memoized flag checking functions
  const isEnabled = useCallback(
    (flag: FeatureFlag): boolean => {
      return flags[flag] ?? FEATURE_FLAG_CONFIG[flag].defaultValue;
    },
    [flags]
  );

  const areEnabled = useCallback(
    (flagsToCheck: FeatureFlag[]): boolean => {
      return flagsToCheck.every(flag => isEnabled(flag));
    },
    [isEnabled]
  );

  const isAnyEnabled = useCallback(
    (flagsToCheck: FeatureFlag[]): boolean => {
      return flagsToCheck.some(flag => isEnabled(flag));
    },
    [isEnabled]
  );

  const getConfig = useCallback((flag: FeatureFlag) => {
    return FEATURE_FLAG_CONFIG[flag];
  }, []);

  // Memoized context value
  const contextValue = useMemo<FeatureFlagContextValue>(
    () => ({
      flags,
      isEnabled,
      areEnabled,
      isAnyEnabled,
      getConfig,
      isInitialized,
    }),
    [flags, isEnabled, areEnabled, isAnyEnabled, getConfig, isInitialized]
  );

  return (
    <FeatureFlagErrorBoundary>
      <FeatureFlagContext.Provider value={contextValue}>
        {children}
      </FeatureFlagContext.Provider>
    </FeatureFlagErrorBoundary>
  );
}

/**
 * Higher-order component to conditionally render components based on feature flags.
 *
 * @example
 * ```tsx
 * const ConditionalComponent = withFeatureFlag({
 *   flag: FeatureFlag.ENABLE_SUBSCRIPTIONS,
 *   children: <SubscriptionFeatures />,
 *   fallback: <div>Subscriptions not available</div>
 * });
 * ```
 */
export function withFeatureFlag({
  flag,
  children,
  fallback = null,
}: {
  flag: FeatureFlag;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  return function FeatureFlagWrapper() {
    const context = React.useContext(FeatureFlagContext);

    if (!context) {
      console.warn(
        '[FeatureFlags] withFeatureFlag used outside of FeatureFlagProvider'
      );
      return <>{fallback}</>;
    }

    const { isEnabled } = context;

    return <>{isEnabled(flag) ? children : fallback}</>;
  };
}

/**
 * Component to conditionally render content based on multiple feature flags.
 *
 * @example
 * ```tsx
 * <FeatureFlagGate flags={[FeatureFlag.ENABLE_SUBSCRIPTIONS]} requireAll>
 *   <SubscriptionContent />
 * </FeatureFlagGate>
 * ```
 */
export function FeatureFlagGate({
  flags,
  children,
  fallback = null,
  requireAll = true,
}: {
  flags: FeatureFlag[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAll?: boolean;
}) {
  const context = React.useContext(FeatureFlagContext);

  if (!context) {
    console.warn(
      '[FeatureFlags] FeatureFlagGate used outside of FeatureFlagProvider'
    );
    return <>{fallback}</>;
  }

  const { areEnabled, isAnyEnabled } = context;
  const shouldRender = requireAll ? areEnabled(flags) : isAnyEnabled(flags);

  return <>{shouldRender ? children : fallback}</>;
}

/**
 * Debug component to display current feature flag states.
 * Useful during development to see which flags are enabled/disabled.
 *
 * @example
 * ```tsx
 * // Only render in development
 * {process.env.NODE_ENV === 'development' && <FeatureFlagDebugger />}
 * ```
 */
export function FeatureFlagDebugger({
  compact = false,
  style = {},
}: {
  compact?: boolean;
  style?: React.CSSProperties;
}) {
  const context = React.useContext(FeatureFlagContext);

  if (!context) {
    return <div style={style}>FeatureFlagDebugger: Not inside provider</div>;
  }

  const { flags, isInitialized } = context;

  if (!isInitialized) {
    return <div style={style}>FeatureFlagDebugger: Initializing...</div>;
  }

  if (compact) {
    const enabledFlags = Object.entries(flags)
      .filter(([, value]) => value)
      .map(([flag]) => flag);

    return (
      <div style={{ fontFamily: 'monospace', fontSize: '12px', ...style }}>
        Flags:{' '}
        {enabledFlags.length > 0 ? enabledFlags.join(', ') : 'none enabled'}
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: 'monospace',
        fontSize: '12px',
        border: '1px solid #ccc',
        padding: '8px',
        margin: '8px 0',
        backgroundColor: '#f9f9f9',
        ...style,
      }}
    >
      <h4 style={{ margin: '0 0 8px 0' }}>Feature Flags Debug</h4>
      {Object.entries(flags).map(([flag, value]) => (
        <div key={flag} style={{ marginBottom: '4px' }}>
          <span style={{ color: value ? 'green' : 'red' }}>
            {value ? '✓' : '✗'}
          </span>{' '}
          {flag}: <strong>{String(value)}</strong>
        </div>
      ))}
      <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
        Initialized: {String(isInitialized)}
      </div>
    </div>
  );
}
